import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Shield, Building2, Copy, Check, Calendar, ExternalLink, CheckCircle2, Loader2, Mail } from 'lucide-react';
import api, { authAPI } from '../../utils/api';
import AddressAutocomplete from '../../components/ui/address-autocomplete';

const CompanySettings = () => {
  const { t } = useTranslation();
  const user = JSON.parse(localStorage.getItem('movemate_user') || '{}');
  const [searchParams] = useSearchParams();
  const [company, setCompany] = useState(null);
  const [mfaEnabled, setMfaEnabled] = useState(user.mfa_enabled || false);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    postal_code: '',
    website: '',
    hourly_rate: 0,
    deposit_percentage: 0
  });
  
  // Calendar Integration State
  const [calendarStatus, setCalendarStatus] = useState({
    configured: false,
    connected: false,
    email: null,
    connected_at: null
  });
  const [calendarLoading, setCalendarLoading] = useState(false);
  
  // 2FA Setup State
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  
  // 2FA Disable State
  const [showDisable2FA, setShowDisable2FA] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');

  useEffect(() => {
    fetchCompanyData();
    fetchCalendarStatus();
    
    // Check for calendar connection result from URL params
    const calendarConnected = searchParams.get('calendar_connected');
    const calendarError = searchParams.get('calendar_error');
    const calendarEmail = searchParams.get('email');
    
    if (calendarConnected === 'true') {
      toast.success(`Google Calendar connected! (${calendarEmail})`);
      fetchCalendarStatus();
    } else if (calendarError) {
      toast.error(`Calendar connection failed: ${calendarError}`);
    }
  }, []);

  const fetchCalendarStatus = async () => {
    try {
      const response = await api.get('/calendar/status');
      setCalendarStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch calendar status');
    }
  };

  const handleConnectCalendar = async () => {
    setCalendarLoading(true);
    try {
      const response = await api.get('/calendar/connect');
      // Response contains authorization_url for redirect
      if (response.data?.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      toast.error('Failed to start calendar connection');
      setCalendarLoading(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    setCalendarLoading(true);
    try {
      await api.post('/calendar/disconnect');
      toast.success('Google Calendar disconnected');
      setCalendarStatus({
        ...calendarStatus,
        connected: false,
        email: null,
        connected_at: null
      });
    } catch (error) {
      toast.error('Failed to disconnect calendar');
    } finally {
      setCalendarLoading(false);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const response = await api.get(`/companies/${user.company_id}`);
      setCompany(response.data);
      setCompanyForm({
        name: response.data.name || '',
        phone: response.data.phone || '',
        address: response.data.address || '',
        city: response.data.city || '',
        country: response.data.country || '',
        postal_code: response.data.postal_code || '',
        website: response.data.website || '',
        hourly_rate: response.data.hourly_rate || 0,
        deposit_percentage: response.data.deposit_percentage || 0
      });
    } catch (error) {
      toast.error('Failed to load company data');
    }
  };

  const handleSaveCompany = async () => {
    setLoading(true);
    try {
      await api.put(`/companies/${user.company_id}`, companyForm);
      toast.success('Company profile updated successfully');
      setEditMode(false);
      fetchCompanyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update company');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    setLoading(true);
    try {
      const response = await authAPI.setup2FA();
      toast.success(response.data.message);
      setShow2FASetup(true);
    } catch (error) {
      toast.error('Failed to send verification email');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    try {
      await authAPI.enable2FA(verificationCode);
      setMfaEnabled(true);
      setShow2FASetup(false);
      setVerificationCode('');
      toast.success('2FA enabled successfully!');
      
      // Update local storage
      const updatedUser = { ...user, mfa_enabled: true };
      localStorage.setItem('movemate_user', JSON.stringify(updatedUser));
    } catch (error) {
      toast.error('Invalid verification code');
    }
  };

  const handleDisable2FA = async () => {
    try {
      await authAPI.disable2FA(disablePassword);
      setMfaEnabled(false);
      setShowDisable2FA(false);
      setDisablePassword('');
      toast.success('2FA disabled');
      
      // Update local storage
      const updatedUser = { ...user, mfa_enabled: false };
      localStorage.setItem('movemate_user', JSON.stringify(updatedUser));
    } catch (error) {
      toast.error('Invalid password');
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedCodes({ ...copiedCodes, [index]: true });
    toast.success('Copied!');
    setTimeout(() => setCopiedCodes({ ...copiedCodes, [index]: false }), 2000);
  };

  return (
    <div className="space-y-6" data-testid="company-settings">
      <div>
        <h1 className="text-4xl font-bold mb-2">{t('settings.title', 'Settings')}</h1>
        <p className="text-gray-600">{t('settings.subtitle', 'Manage your company settings and security')}</p>
      </div>

      <Tabs defaultValue="company" className="space-y-6">
        <TabsList>
          <TabsTrigger value="company">{t('settings.company_profile', 'Company Profile')}</TabsTrigger>
          <TabsTrigger value="calendar">{t('settings.calendar', 'Calendar')}</TabsTrigger>
          <TabsTrigger value="security">{t('settings.security', 'Security')}</TabsTrigger>
        </TabsList>

        {/* Company Profile Tab */}
        <TabsContent value="company">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 size={24} />
                {t('settings.company_info', 'Company Information')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {company ? (
                <div className="space-y-4">
                  {editMode ? (
                    <>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Company Name</Label>
                          <Input 
                            value={companyForm.name} 
                            onChange={(e) => setCompanyForm({...companyForm, name: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Phone</Label>
                          <Input 
                            value={companyForm.phone} 
                            onChange={(e) => setCompanyForm({...companyForm, phone: e.target.value})}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Address</Label>
                        <AddressAutocomplete
                          value={companyForm.address}
                          onChange={(e) => setCompanyForm({...companyForm, address: e.target.value})}
                          onAddressSelect={(addr) => setCompanyForm({
                            ...companyForm,
                            address: addr.street || addr.formatted_address || '',
                            city: addr.city || companyForm.city,
                            postal_code: addr.postal_code || companyForm.postal_code,
                            country: addr.country || companyForm.country
                          })}
                          placeholder="Start typing your address..."
                          data-testid="company-address-autocomplete"
                        />
                      </div>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label>City</Label>
                          <Input 
                            value={companyForm.city} 
                            onChange={(e) => setCompanyForm({...companyForm, city: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Country</Label>
                          <Input 
                            value={companyForm.country} 
                            onChange={(e) => setCompanyForm({...companyForm, country: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Postal Code</Label>
                          <Input 
                            value={companyForm.postal_code} 
                            onChange={(e) => setCompanyForm({...companyForm, postal_code: e.target.value})}
                          />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Website</Label>
                          <Input 
                            value={companyForm.website} 
                            onChange={(e) => setCompanyForm({...companyForm, website: e.target.value})}
                            placeholder="https://yourcompany.com"
                          />
                        </div>
                        <div>
                          <Label>Hourly Rate (£)</Label>
                          <Input 
                            type="number"
                            value={companyForm.hourly_rate} 
                            onChange={(e) => setCompanyForm({...companyForm, hourly_rate: parseFloat(e.target.value) || 0})}
                          />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Deposit Percentage (%)</Label>
                          <Input 
                            type="number"
                            value={companyForm.deposit_percentage} 
                            onChange={(e) => setCompanyForm({...companyForm, deposit_percentage: parseFloat(e.target.value) || 0})}
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                        <Button onClick={handleSaveCompany} disabled={loading}>
                          {loading ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Company Name</Label>
                          <Input value={company.name} disabled />
                        </div>
                        <div>
                          <Label>Email</Label>
                          <Input value={company.email} disabled />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Phone</Label>
                          <Input value={company.phone || ''} disabled />
                        </div>
                        <div>
                          <Label>City</Label>
                          <Input value={company.city || ''} disabled />
                        </div>
                      </div>
                      <div>
                        <Label>Address</Label>
                        <Input value={company.address || ''} disabled />
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Hourly Rate</Label>
                          <Input value={`£${company.hourly_rate || 0}/hr`} disabled />
                        </div>
                        <div>
                          <Label>Deposit</Label>
                          <Input value={`${company.deposit_percentage || 0}%`} disabled />
                        </div>
                      </div>
                      <Button variant="outline" onClick={() => setEditMode(true)} data-testid="edit-company-btn">
                        Edit Company Profile
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">Loading...</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Calendar Integration Tab */}
        <TabsContent value="calendar">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar size={24} />
                Google Calendar Integration
              </CardTitle>
              <CardDescription>
                Sync your jobs automatically to Google Calendar
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {!calendarStatus.configured ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
                  <h3 className="font-semibold text-gray-900 mb-2">Calendar Integration Not Configured</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Contact support to enable Google Calendar integration for your account.
                  </p>
                </div>
              ) : calendarStatus.connected ? (
                <div className="space-y-4">
                  {/* Connected Status */}
                  <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                        <CheckCircle2 className="text-green-600" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-green-900">Connected</h3>
                        <p className="text-sm text-green-700">{calendarStatus.email}</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleDisconnectCalendar}
                      disabled={calendarLoading}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      {calendarLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                      Disconnect
                    </Button>
                  </div>

                  {/* Connection Info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <Label className="text-gray-500 text-xs">Connected Email</Label>
                      <p className="font-medium">{calendarStatus.email}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <Label className="text-gray-500 text-xs">Connected On</Label>
                      <p className="font-medium">
                        {calendarStatus.connected_at 
                          ? new Date(calendarStatus.connected_at).toLocaleDateString('en-GB', {
                              day: 'numeric', month: 'short', year: 'numeric'
                            })
                          : 'Unknown'}
                      </p>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3">What gets synced:</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 size={16} className="text-green-500" />
                        New jobs automatically create calendar events
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 size={16} className="text-green-500" />
                        Job updates reflect in calendar
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 size={16} className="text-green-500" />
                        Pickup & delivery addresses in event details
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 size={16} className="text-green-500" />
                        Assigned staff shown in event description
                      </li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Not Connected */}
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
                    <h3 className="font-semibold text-gray-900 mb-2">Connect Google Calendar</h3>
                    <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
                      Automatically sync your MoveMate jobs to Google Calendar. 
                      Keep your team's schedule up to date without manual entry.
                    </p>
                    <Button
                      onClick={handleConnectCalendar}
                      disabled={calendarLoading}
                      className="gap-2"
                      data-testid="connect-calendar-btn"
                    >
                      {calendarLoading ? (
                        <Loader2 className="animate-spin" size={16} />
                      ) : (
                        <ExternalLink size={16} />
                      )}
                      Connect Google Calendar
                    </Button>
                  </div>

                  {/* Benefits */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3">Benefits:</h4>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 size={16} className="text-primary" />
                        View all jobs in your Google Calendar
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 size={16} className="text-primary" />
                        Get reminders on your phone
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 size={16} className="text-primary" />
                        Share calendar with your team
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 size={16} className="text-primary" />
                        One-way sync (MoveMate → Calendar)
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield size={24} />
                Two-Factor Authentication
              </CardTitle>
              <CardDescription>
                Add an extra layer of security to your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">2FA Status</h3>
                  <p className="text-sm text-gray-600">
                    {mfaEnabled
                      ? 'Your account is protected with 2FA'
                      : 'Enable 2FA for enhanced security'}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <Switch
                    checked={mfaEnabled}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        handleEnable2FA();
                      } else {
                        setShowDisable2FA(true);
                      }
                    }}
                    disabled={loading}
                    data-testid="2fa-toggle"
                  />
                  <span className={`text-sm font-medium ${
                    mfaEnabled ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    {mfaEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              {mfaEnabled && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    ✓ Your account is secured with two-factor authentication
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* 2FA Setup Dialog */}
      <Dialog open={show2FASetup} onOpenChange={setShow2FASetup}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              A verification code has been sent to your email address. Enter the code below to enable 2FA.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Email Sent Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-800">
                <Mail className="h-5 w-5" />
                <span className="font-medium">Check your email</span>
              </div>
              <p className="text-sm text-blue-700 mt-2">
                We've sent a 6-digit verification code to your email. The code expires in 10 minutes.
              </p>
            </div>

            {/* Verification */}
            <div>
              <Label htmlFor="verification_code">Enter 6-Digit Code from Email</Label>
              <Input
                id="verification_code"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="text-center text-2xl tracking-widest mt-2"
                autoFocus
              />
            </div>

            <Button
              onClick={handleVerify2FA}
              className="w-full"
              disabled={verificationCode.length !== 6}
            >
              Verify & Enable 2FA
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 2FA Disable Dialog */}
      <Dialog open={showDisable2FA} onOpenChange={setShowDisable2FA}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Enter your password to disable 2FA
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="disable_password">Password</Label>
              <Input
                id="disable_password"
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                placeholder="Enter your password"
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowDisable2FA(false);
                  setDisablePassword('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleDisable2FA}
                disabled={!disablePassword}
              >
                Disable 2FA
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanySettings;
